import React, { useState } from 'react'
import { Modal } from '../UI/Modal/Modal'
import {
  useGetSampleRequest,
  useCancelSampleRequest,
} from '../../services/samples'
import type { SampleRequestDetail } from '../../models/samples'
import { SampleRequestPreview } from './SampleRequestPreview'
import { Spinner } from 'react-bootstrap'
import { CancelSampleRequestModal } from './CancelSampleRequestModal'
import { toast } from 'react-toastify'
import to from 'await-to-js'

interface SampleRequestPreviewModalProps {
  show: boolean
  onHide: () => void
  sampleRequestId: number | null
}

export function SampleRequestPreviewModal({
  show,
  onHide,
  sampleRequestId,
}: SampleRequestPreviewModalProps) {
  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)

  const cancelMutation = useCancelSampleRequest()

  const { data: order, isLoading } = useGetSampleRequest(
    { id: sampleRequestId ?? 0 },
    { query: { enabled: !!sampleRequestId && show } }
  )

  const handleCancelClick = () => {
    setCancelModalOpen(true)
  }

  const handleCancelConfirm = async (reason: string) => {
    if (!sampleRequestId) return

    setIsCancelling(true)
    const [err] = await to(
      cancelMutation.mutateAsync({
        id: sampleRequestId,
        data: { cancellation_reason: reason },
      })
    )

    setIsCancelling(false)

    if (!err) {
      toast.success('Sample request cancelled successfully')
      setCancelModalOpen(false)
      // Detail + list caches are invalidated by the mutation registry.
    } else {
      toast.error('Failed to cancel sample request')
    }
  }

  const canCancel = order?.status === 'submitted'

  return (
    <>
      <Modal
        open={show}
        onOpenChange={(open) => !open && onHide()}
        title="Sample Request Details"
        description="View sample request information"
        size="lg"
        hideCancelButton={!canCancel}
        onAlt={canCancel ? handleCancelClick : undefined}
        altButtonText="Cancel Request"
        altButtonProps={{ variant: 'danger' }}
      >
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : order ? (
          <SampleRequestPreview
            order={order as unknown as SampleRequestDetail}
            showHeader={true}
          />
        ) : (
          <div className="text-center py-5 text-muted">
            Sample order not found
          </div>
        )}
      </Modal>

      <CancelSampleRequestModal
        open={cancelModalOpen}
        onOpenChange={setCancelModalOpen}
        onConfirm={handleCancelConfirm}
        isSubmitting={isCancelling}
        requestNumber={order?.request_number}
      />
    </>
  )
}
